import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { fmtDate, fmtTime } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const th = 'text-left text-[10px] uppercase tracking-wider text-brand-muted font-medium px-3 py-2';
const when = (s) => { const [d, t] = String(s || '').split(' '); return `${fmtDate(d)} às ${fmtTime(t)}`; };

export default function GcalDuplicatesTab() {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/api/admin/gcal/duplicates`).then(r => { setRows(r.data.duplicates); setPicked([]); }).catch(() => toast.error('Erro ao procurar duplicados')), []);
  useEffect(() => { load(); }, [load]);
  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/gcal/dedupe-rescheduled`, { appointment_ids: picked, confirm: true });
      const failed = data.results.filter(r => r.error).length;
      toast[failed ? 'warning' : 'success'](`${data.removed} evento(s) apagado(s)${failed ? `, ${failed} com erro` : ''}`);
      setConfirming(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao apagar'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="gcal-duplicates-tab">
      <div><h2 className="font-heading font-medium text-xl text-brand-text">Google Calendar: eventos duplicados</h2>
        <p className="text-xs text-brand-muted">Consultas remarcadas cujo evento antigo ficou no calendário ao lado do novo. Escolhe os que queres apagar. Nada é apagado sem confirmares.</p></div>
      {!rows ? <p className="text-xs text-brand-muted">A procurar...</p> : !rows.length ? (
        <p className="text-sm text-brand-muted bg-white border border-brand-border rounded-2xl p-4" data-testid="gcal-dup-empty">Não há eventos duplicados.</p>
      ) : (
        <>
          <div className="bg-white border border-brand-border rounded-2xl overflow-x-auto" data-testid="gcal-dup-table">
            <table className="w-full text-sm"><thead className="bg-brand-bg"><tr><th className={th}></th>{['Paciente', 'Data antiga (a apagar)', 'Data nova (mantém-se)'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>{rows.map(r => (
                <tr key={r.appointment_id} className="border-t border-brand-border/50" data-testid={`gcal-dup-row-${r.appointment_id}`}>
                  <td className="px-3 py-2"><input type="checkbox" checked={picked.includes(r.appointment_id)} onChange={() => toggle(r.appointment_id)} data-testid={`gcal-dup-check-${r.appointment_id}`} /></td>
                  <td className="px-3 py-2">{r.patient}</td><td className="px-3 py-2">{when(r.old)}</td><td className="px-3 py-2">{when(r.new)}</td></tr>))}</tbody></table>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setPicked(picked.length === rows.length ? [] : rows.map(r => r.appointment_id))} className="text-xs border border-brand-border rounded-full px-3 py-1.5" data-testid="gcal-dup-select-all">{picked.length === rows.length ? 'Limpar seleção' : 'Selecionar todos'}</button>
            <button disabled={!picked.length} onClick={() => setConfirming(true)} className="bg-red-600 text-white rounded-full px-4 py-1.5 text-xs font-medium disabled:opacity-40" data-testid="gcal-dup-delete-btn">Apagar selecionados ({picked.length})</button>
          </div>
        </>
      )}
      {confirming && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="gcal-dup-confirm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
            <h3 className="font-heading text-lg text-brand-text">Apagar {picked.length} evento(s)?</h3>
            <p className="text-sm text-brand-muted">Os eventos antigos serão apagados do Google Calendar, sem enviar avisos às pacientes. Esta ação não se pode desfazer.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="border border-brand-border rounded-full px-4 py-2 text-sm" data-testid="gcal-dup-cancel">Cancelar</button>
              <button onClick={remove} disabled={busy} className="bg-red-600 text-white rounded-full px-4 py-2 text-sm" data-testid="gcal-dup-confirm-btn">{busy ? 'A apagar...' : 'Apagar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
